import type { FaqItem } from '@nhanho/shared'
import { Button } from '../ui/Button'
import { Input, Textarea } from '../ui/Input'
import { Field } from '../ui/Field'

/**
 * Câu hỏi thường gặp → `apps/web` sinh schema `FAQPage` khi có **từ 2 câu trở lên**.
 *
 * Vì sao đáng làm: khối FAQ chiếm thêm diện tích trên trang kết quả Google (nhiều dòng
 * hơn đối thủ) và trả lời sẵn thắc mắc trước khi khách phải nhắn Zalo.
 * Câu trả lời là **text thuần** — schema.org cần văn bản, và không nhận HTML thì không
 * mở thêm đường XSS.
 */
export function FaqEditor({
  items, onChange, errors = {},
}: { items: FaqItem[]; onChange: (items: FaqItem[]) => void; errors?: Record<string, string> }) {
  const update = (i: number, patch: Partial<FaqItem>) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i))
  const move = (i: number, dir: -1 | 1) => {
    const to = i + dir
    if (to < 0 || to >= items.length) return
    const next = [...items]
    const [it] = next.splice(i, 1)
    next.splice(to, 0, it!)
    onChange(next)
  }

  return (
    <div>
      <p className="mb-3 text-xs text-ink-muted">
        Viết đúng câu khách hay hỏi (dùng chính từ ngữ của khách): "Móc khoá in 3D có bền không?", "Khắc tên mất bao lâu?".
        Từ 2 câu trở lên sẽ sinh khối FAQ trên Google.
      </p>
      {items.length === 0 ? (
        <div className="rounded-card-sm border border-dashed border-border-strong p-6 text-center text-sm text-ink-muted">Chưa có câu hỏi nào.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((it, i) => (
            <li key={i} className="rounded-card-sm border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-ink-muted">Câu {i + 1}</span>
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Lên">↑</Button>
                  <Button variant="ghost" size="sm" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Xuống">↓</Button>
                  <Button variant="ghost" size="sm" className="text-danger" onClick={() => remove(i)} aria-label="Xoá câu hỏi">✕</Button>
                </div>
              </div>
              <div className="space-y-2">
                <Field label="Câu hỏi" required error={errors[`faq.${i}.question`]}>
                  <Input value={it.question} maxLength={300} onChange={(e) => update(i, { question: e.target.value })} placeholder="Móc khoá in 3D có bền không?" />
                </Field>
                <Field label="Trả lời" required error={errors[`faq.${i}.answer`]} hint="Text thuần, 2–4 câu, trả lời thẳng vào việc">
                  <Textarea value={it.answer} maxLength={2000} onChange={(e) => update(i, { answer: e.target.value })} className="min-h-20" />
                </Field>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Button variant="secondary" size="sm" className="mt-3" disabled={items.length >= 20} onClick={() => onChange([...items, { question: '', answer: '' }])}>
        + Thêm câu hỏi
      </Button>
    </div>
  )
}
